import type { ConnectionState } from '../../api/realtime';
import type { ReachabilityState } from '../../api/client';
import type { InlineAlertVariant } from '../../ui/InlineAlert';
import type { MessageKey } from '../../i18n/messages.en.ts';

function formatAgo(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// MVP-11 #16: 'info' added alongside the existing four for `reauth` — DESIGN.md §6.4's table
// gives `reauth` the `--info-500` dot, not amber (confirmed live as a seam: cohesion-r1.md #16
// "`reauth` amber not info"). New CSS class needed: `.connection-indicator__dot--info` (H owns
// components.css; the other four dot classes already exist there).
export type IndicatorTone = 'ok' | 'amber' | 'red' | 'neutral' | 'info';

// MVP-11 #16 cross-package contract 1: the guest page (Package F, `QueuePositionCard`) imports
// `connectionLabel` below instead of inventing its own connection copy. Deliberately a smaller,
// four-state vocabulary than raw `ConnectionState`/`ReachabilityState` — every caller (this
// module's own `deriveConnectionStatus`, and the guest's own REST/SSE reconciliation) is
// responsible for reducing its own signals down to one of these before asking for copy.
export type ConnectionLabelState = 'online' | 'reconnecting' | 'reauth' | 'offline';

/**
 * DESIGN.md §6.4's one connection-language table, as reusable copy. `variant` lines up 1:1 with
 * the table's dot colours and with `InlineAlertVariant`: ok=silent, accent=degraded/reconnecting,
 * info=reauth, danger=offline. MVP-11 #16: this app does NOT disable controls app-wide while
 * offline (the queue/rotation panels already surface their own write failures) — the offline copy
 * says so rather than claiming a disabled state that does not exist.
 *
 * MVP-272: keys, not words. This module is deliberately plain `.ts` so it is importable under
 * `node --test` (see `deriveConnectionStatus`'s own note below), which means it has no provider to
 * read a locale from — and it is imported by the guest page, the cockpit indicator and `App.tsx`
 * alike, so a sentence built here would be one language for all three readers. `descriptionKey` is
 * `null`, not an empty key, for the two states that have nothing more to say.
 */
export function connectionLabel(state: ConnectionLabelState): { titleKey: MessageKey; descriptionKey: MessageKey | null; variant: InlineAlertVariant } {
  switch (state) {
    case 'online':
      return { titleKey: 'common.connection.connected', descriptionKey: null, variant: 'ok' };
    case 'reconnecting':
      return { titleKey: 'common.connection.reconnecting', descriptionKey: 'common.connection.reconnectingDetail', variant: 'accent' };
    case 'reauth':
      return { titleKey: 'common.connection.reauth', descriptionKey: null, variant: 'info' };
    case 'offline':
      return { titleKey: 'common.connection.offline', descriptionKey: 'common.connection.offlineDetail', variant: 'danger' };
  }
}

export interface DerivedConnectionStatus {
  tone: IndicatorTone;
  labelKey: MessageKey | null;
  /** Rendered after `labelKey` with a ` · ` between them — either the degraded staleness readout or
   * the offline host blame, never both (DESIGN.md §6.4's "name the exact failing host" rule only
   * applies to a genuine REST-reachability failure, not a merely-reconnecting SSE stream). `null`
   * when there is nothing to add; the separator belongs to the component that joins them, since a
   * detail-less status must not render a dangling one. */
  detail: { key: MessageKey; params: Record<string, string | number> } | null;
}

/**
 * R9-P1-D fix (round 9 critic, reproduced live): `ConnectionIndicator.tsx` used to read `LABEL[state]`
 * straight off `RealtimeClient`'s OWN state — confirmed live, the footer read "Offline · backend-mv...biz
 * unreachable" while every REST call in the network tab was a clean 200. An SSE stream dying is
 * not the same fact as the backend being unreachable: this is the one place those two signals
 * (REST reachability from `client.ts`'s `doFetch`, and the SSE connection state from
 * `RealtimeClient`) get reconciled into what the KJ actually sees. Kept in its own plain `.ts`
 * module (not inline in the `.tsx` component) purely so it is importable under `node --test`
 * without a JSX/TSX loader — the same reason every OTHER pure decision in this directory
 * (`clock.ts`, `hotkeys.ts`, `stageChannel.ts`, ...) lives next to, not inside, its component.
 *
 * Precedence, most to least severe:
 *  1. `navigator.onLine === false` — the OS/browser itself says there is no network at all.
 *  2. REST `reachability === 'unreachable'` — the actual signal a KJ needs "Offline" for: a real
 *     `apiFetch` call just failed to even reach the server (DESIGN.md §6.4's "name the exact
 *     failing host" copy belongs here, never anywhere else).
 *  3. The SSE connection alone being unhappy (`degraded`/`offline`/`reauth`) is a SEPARATE,
 *     lower-severity fact while REST keeps working — every queue/rotation/playback action still
 *     goes through as the KJ clicks it, it just will not update live from OTHER consoles/the guest
 *     page until the stream recovers. Amber, "Live updates reconnecting…", never red "Offline".
 *  4. Otherwise (`connecting`/`online` and REST reachable-or-still-unknown) — silence, just the dot
 *     (DESIGN.md §6.4: "silence is the reward for working").
 */
export function deriveConnectionStatus(params: {
  realtimeState: ConnectionState;
  reachability: ReachabilityState;
  online: boolean;
  targetHost: string;
  sinceMs: number;
  nowMs: number;
}): DerivedConnectionStatus {
  const { realtimeState, reachability, online, targetHost, sinceMs, nowMs } = params;

  if (!online) {
    return { tone: 'red', labelKey: connectionLabel('offline').titleKey, detail: null };
  }
  if (reachability === 'unreachable') {
    return { tone: 'red', labelKey: connectionLabel('offline').titleKey, detail: { key: 'common.connection.hostUnreachable', params: { host: targetHost } } };
  }
  if (realtimeState === 'reauth') {
    return { tone: 'info', labelKey: connectionLabel('reauth').titleKey, detail: null };
  }
  if (realtimeState === 'degraded') {
    return { tone: 'amber', labelKey: connectionLabel('reconnecting').titleKey, detail: { key: 'common.connection.updatedAgo', params: { age: formatAgo(nowMs - sinceMs) } } };
  }
  if (realtimeState === 'offline') {
    return { tone: 'amber', labelKey: connectionLabel('reconnecting').titleKey, detail: null };
  }
  if (realtimeState === 'connecting') {
    return { tone: 'neutral', labelKey: null, detail: null };
  }
  return { tone: 'ok', labelKey: null, detail: null };
}

/**
 * MVP-483. How far an outage actually reaches — the missing half of `deriveConnectionStatus` above,
 * which answers "what does the status bar say" but not "which part of this screen is lying".
 *
 *  - `'app'`    — REST itself cannot be reached (or the OS says there is no network). NOTHING on
 *                 screen is being refreshed by anything, so every surface is stale and the notice
 *                 is app-wide: the cockpit's full-width top strip, the Stage veil, the guest veil.
 *  - `'region'` — the SSE stream alone is unhappy while REST keeps answering. The data is NOT
 *                 stale: `useRotationState.ts`'s 15s poll, `OperatorConsole.tsx`'s 10s tray poll
 *                 and `usePlaybackState.ts`'s Stage fallback all keep running precisely because
 *                 `shouldPollWhileDisconnected` arms them when the stream is down. Only the
 *                 live-update guarantee for that ONE region is gone, so it is said inline beside
 *                 the region and never promoted to a banner — MVP-48 and MVP-172 were both the
 *                 same regression of an SSE drop being shown to the KJ as the backend being down.
 *  - `'none'`   — silence, §6.4's reward for working.
 *
 * Precedence is `deriveConnectionStatus`'s, unchanged, and deliberately shares its vocabulary
 * rather than inventing a second one: `deriveOutage` below resolves every scope back into one of
 * `connectionLabel`'s four states.
 */
export type OutageScope = 'none' | 'app' | 'region';

export interface OutageInput {
  /** `null` for a surface with no realtime client of its own — the Stage window, which reads live
   * data over `BroadcastChannel` and its hooks' own polls (`App.tsx` starts `realtimeClient` for
   * the console route only). A surface with no stream can never be in `'region'` scope. */
  realtimeState: ConnectionState | null;
  reachability: ReachabilityState;
  online: boolean;
}

export function outageScope({ realtimeState, reachability, online }: OutageInput): OutageScope {
  if (!online) return 'app';
  if (reachability === 'unreachable') return 'app';
  if (realtimeState === 'degraded' || realtimeState === 'offline' || realtimeState === 'reauth') return 'region';
  return 'none';
}

export interface DerivedOutage {
  scope: OutageScope;
  /** Which of `connectionLabel`'s four states this outage speaks in — the extension point, rather
   * than a second set of words. */
  state: ConnectionLabelState;
  titleKey: MessageKey;
  descriptionKey: MessageKey | null;
  variant: InlineAlertVariant;
  /** The machine to blame, for `common.connection.hostUnreachable`. `null` unless a REST call
   * genuinely failed to reach it: when `navigator.onLine` is false there is no failing host — the
   * browser never dialled one — and naming one would be a guess presented as a diagnosis. */
  host: string | null;
}

export function deriveOutage(params: OutageInput & { targetHost: string }): DerivedOutage {
  const scope = outageScope(params);
  const state: ConnectionLabelState =
    scope === 'app' ? 'offline' : scope === 'region' ? (params.realtimeState === 'reauth' ? 'reauth' : 'reconnecting') : 'online';
  const { titleKey, descriptionKey, variant } = connectionLabel(state);
  const host = scope === 'app' && params.online && params.reachability === 'unreachable' ? params.targetHost : null;
  return { scope, state, titleKey, descriptionKey, variant, host };
}

/**
 * MVP-483. What the Stage window shows during an app-scope outage. Nobody is standing at the Stage
 * screen — it is a second monitor pointed at a room — so there is no button and no error code here;
 * the only question is whether covering the picture costs more than the honesty buys.
 *
 *  - Actively playing: it does. The singer is mid-song and the lyrics ARE the screen. A stale
 *    connection does not stop the local `<video>` decoding, so the picture is not a lie; a
 *    low-contrast pill in `.stage-paused-badge`'s existing vocabulary says so without taking the
 *    words off the screen.
 *  - Anything else (paused, loading, hold, attract): a frozen last frame or a still attract slide
 *    with no connection behind it looks exactly like a working screen, which is the defect this
 *    task exists to close. Dim the video and put a spinner and one sentence over it — Paramount+'s
 *    pattern, where the video stays visible underneath. NOT HBO Max's bare spinner on black, which
 *    reads as a crashed player rather than a waiting one.
 */
export type StageOutageDisplay = { kind: 'none' } | { kind: 'pill'; labelKey: MessageKey } | { kind: 'veil'; labelKey: MessageKey };

/**
 * MVP-483 follow-up. The one input `stageOutageDisplay` below actually needs, and the one a caller
 * is most likely to get wrong: the `isPlaying` flag on the shared playback state is MIRRORED from
 * the console (BroadcastChannel / SSE / `readStageMirror`), so during an app-scope outage it is
 * exactly the value that has stopped changing. Feeding it straight in is how the Stage ended up
 * with the defect the veil exists to close — backend dies mid-song, the flag freezes at `true`, the
 * song runs to its end locally, nothing advances because advancing needs the console, and the room
 * is left staring at a frozen last frame wearing a low-contrast "Reconnecting" pill forever.
 *
 * The pill is only honest while the local `<video>` is genuinely decoding, so all four facts have
 * to agree: the console says playing AND the element has not ended, is not paused, and is not
 * sitting behind the autoplay gesture gate (`needsGesture`, which leaves the element paused while
 * the mirrored flag happily says playing).
 */
export function stageVideoIsLive({
  mirroredIsPlaying,
  localEnded,
  localPaused,
  needsGesture,
}: {
  mirroredIsPlaying: boolean;
  localEnded: boolean;
  localPaused: boolean;
  needsGesture: boolean;
}): boolean {
  return mirroredIsPlaying && !localEnded && !localPaused && !needsGesture;
}

export function stageOutageDisplay({ scope, isPlaying }: { scope: OutageScope; isPlaying: boolean }): StageOutageDisplay {
  // Region scope is unreachable from the Stage (it has no stream of its own, see `OutageInput`) and
  // would be meaningless if it were: there is no "region" on a screen that is one picture.
  if (scope !== 'app') return { kind: 'none' };
  if (isPlaying) return { kind: 'pill', labelKey: 'common.outage.stagePill' };
  return { kind: 'veil', labelKey: 'common.outage.stageVeil' };
}

/**
 * MVP-11 #9: every console poll this wave removes now has a live SSE frame behind it (see
 * `mvp-11-wave2.md`'s "Facts established before planning") — the four polls in
 * `useRotationState.ts`/`OperatorConsole.tsx`/`usePlaybackState.ts`(console role)/
 * `useSessionInfo.ts` become a pure safety net for a dead stream, not an unconditional timer.
 * `'connecting'` counts as disconnected too — nothing has proven the stream up yet.
 */
export function shouldPollWhileDisconnected(state: ConnectionState): boolean {
  return state !== 'online';
}
